import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import type { CurrentMember, UiLocale } from '../shared/models';
import type { CreateWorkspaceInput, WorkspaceSettings, WorkspaceSummary } from '../shared/workspaces';
import { api, ApiError } from './api';
import { ACTIVE_WORKSPACE_STORAGE_KEY, activeWorkspaceId, setActiveWorkspaceId, AUTH_ACCESS_CHANGED_EVENT, AUTH_UNAUTHORIZED_EVENT } from './shared/api-client';
import { parseAppRoute, pathForRoute, type AppRoute, type AppScreen } from './routes';
import { appHistoryScrollState, appHistoryState, nextAppHistoryIndex, readAppHistoryIndex, readAppHistoryScrollY, restorationDelta } from './navigation-history';
import { closeOverlayBeforeNavigation, handleOverlayPopState } from './shared/overlay-history';
import { BootstrapPage } from './pages/BootstrapPage';
import { LoginPage } from './pages/LoginPage';
import { InviteAcceptPage } from './pages/InviteAcceptPage';
import { NoWorkspacePage } from './pages/NoWorkspacePage';
import { AccountPage, ForcedPasswordChangePage } from './features/account/AccountPage';
import { AccessDeniedState } from './components/AccessDeniedState';
import { AppShell, type ShellAccess } from './components/AppShell';
import { useI18n } from './i18n';
import { useUnsavedChangesGuard } from './unsaved-changes';
import { TaskProvider } from './tasks/TaskProvider';
import { PageLoadingSkeleton } from './components/LoadingSkeleton';

const loadHomePage = () => import('./pages/HomePage');
const loadLibraryPage = () => import('./pages/LibraryPage');
const loadIngredientsPage = () => import('./pages/IngredientsPage');
const loadRecipesPage = () => import('./pages/RecipesPage');
const loadDishesPage = () => import('./pages/DishesPage');
const loadMenusPage = () => import('./pages/MenusPage');
const loadImportPage = () => import('./pages/ImportPage');
const loadScalingPage = () => import('./pages/ScalingPage');
const loadMorePage = () => import('./pages/MorePage');

const HomePage = lazy(() => loadHomePage().then(module => ({ default: module.HomePage })));
const LibraryPage = lazy(() => loadLibraryPage().then(module => ({ default: module.LibraryPage })));
const IngredientsPage = lazy(() => loadIngredientsPage().then(module => ({ default: module.IngredientsPage })));
const RecipesPage = lazy(() => loadRecipesPage().then(module => ({ default: module.RecipesPage })));
const DishesPage = lazy(() => loadDishesPage().then(module => ({ default: module.DishesPage })));
const MenusPage = lazy(() => loadMenusPage().then(module => ({ default: module.MenusPage })));
const ImportPage = lazy(() => loadImportPage().then(module => ({ default: module.ImportPage })));
const ScalingPage = lazy(() => loadScalingPage().then(module => ({ default: module.ScalingPage })));
const ExportCenterPage = lazy(() => import('./pages/ExportCenterPage').then(module => ({ default: module.ExportCenterPage })));
const MembersPage = lazy(() => import('./pages/MembersPage').then(module => ({ default: module.MembersPage })));
const RolesPage = lazy(() => import('./pages/RolesPage').then(module => ({ default: module.RolesPage })));
const AuditPage = lazy(() => import('./pages/AuditPage').then(module => ({ default: module.AuditPage })));
const DomainTrashPage = lazy(() => import('./pages/DomainTrashPage').then(module => ({ default: module.DomainTrashPage })));
const MediaRecyclePage = lazy(() => import('./pages/MediaRecyclePage').then(module => ({ default: module.MediaRecyclePage })));
const WorkspaceSettingsPage = lazy(() => import('./pages/WorkspaceSettingsPage').then(module => ({ default: module.WorkspaceSettingsPage })));
const MorePage = lazy(() => loadMorePage().then(module => ({ default: module.MorePage })));

const PAGE_PRELOADERS: Partial<Record<AppScreen, () => Promise<unknown>>> = {
  home: loadHomePage,
  library: loadLibraryPage,
  ingredients: loadIngredientsPage,
  recipes: loadRecipesPage,
  dishes: loadDishesPage,
  menus: loadMenusPage,
  imports: loadImportPage,
  scaling: loadScalingPage,
  more: loadMorePage
};

function preloadScreen(screen: AppScreen): void {
  void PAGE_PRELOADERS[screen]?.().catch(() => undefined);
}

function canOpenRoute(screen: AppRoute['screen'], access: ShellAccess, isOwner: boolean): boolean {
  if (['home', 'account', 'more', 'invite'].includes(screen)) return true;
  if (screen === 'workspace-settings') return isOwner;
  if (['library', 'ingredients', 'recipes', 'dishes', 'scaling'].includes(screen)) return access.recipeView;
  if (screen === 'menus') return access.menuView;
  if (screen === 'imports') return access.researchView;
  if (screen === 'exports') return access.recipeExport || access.menuExport || access.dataExportAll || access.historyRestore;
  if (screen === 'members') return access.memberManage;
  if (screen === 'roles') return access.permissionManage;
  if (screen === 'audit') return access.auditView;
  if (screen === 'trash') return access.mediaRestore;
  if (screen === 'media-recycle') return access.mediaRestore || access.mediaPurge;
  return false;
}

export function App() {
  const { applyAccountLocale, locale } = useI18n(); const confirmDiscard = useUnsavedChangesGuard();
  const [member, setMember] = useState<CurrentMember | null>(null); const [environment, setEnvironment] = useState('');
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]); const [currentWorkspaceId, setCurrentWorkspace] = useState<string | null>(null);
  const [canCreateWorkspace, setCanCreateWorkspace] = useState(false);
  const [bootstrapping, setBootstrapping] = useState<boolean | null>(null); const [route, setRoute] = useState<AppRoute>(() => parseAppRoute(window.location.pathname));
  const [accessChangedNotice, setAccessChangedNotice] = useState(false);
  const routeRef = useRef(route);
  const historyIndexRef = useRef(readAppHistoryIndex(window.history.state) ?? 0);
  const restoringHistoryRef = useRef(false);

  async function hydrateWorkspaces(
    baseMember: CurrentMember,
    directory?: Awaited<ReturnType<typeof api.workspaces>>
  ): Promise<CurrentMember> {
    const result = directory ?? await api.workspaces();
    const available = result.workspaces.filter(item => item.status === 'active' && item.membershipStatus === 'active');
    const stored = activeWorkspaceId();
    const selected = available.find(item => item.id === stored)?.id
      ?? available.find(item => item.id === baseMember.workspaceId)?.id
      ?? available[0]?.id
      ?? null;
    setWorkspaces(result.workspaces);
    setCanCreateWorkspace(result.canCreateWorkspace);
    setCurrentWorkspace(selected);
    setActiveWorkspaceId(selected);
    if (!selected) return { ...baseMember, workspaceId: null, roles: [], permissions: [] };
    if (baseMember.workspaceId === selected) return baseMember;
    return (await api.me()).member;
  }

  async function refreshWorkspaceContext(preferredId?: string | null): Promise<void> {
    if (!member) return;
    const directory = await api.workspaces();
    const available = directory.workspaces.filter(item => item.status === 'active' && item.membershipStatus === 'active');
    const preferred = preferredId === undefined ? currentWorkspaceId : preferredId;
    const selected = (preferred ? available.find(item => item.id === preferred)?.id : null)
      ?? available[0]?.id
      ?? null;
    setWorkspaces(directory.workspaces);
    setCanCreateWorkspace(directory.canCreateWorkspace);
    setCurrentWorkspace(selected);
    setActiveWorkspaceId(selected);
    if (!selected) {
      setMember({ ...member, workspaceId: null, roles: [], permissions: [] });
      return;
    }
    const refreshed = await api.me();
    setMember(refreshed.member);
  }

  async function loadSession() {
    try {
      // /api/workspaces is intentionally gated while a temporary password still needs to be
      // changed. Resolve identity first so that a valid must-change-password session cannot be
      // mistaken for an unauthenticated bootstrap failure on reload.
      const result = await api.me();
      applyAccountLocale(result.member.preferredLocale);
      setEnvironment(result.environment);
      if (result.member.mustChangePassword) {
        setMember(result.member); setWorkspaces([]); setCanCreateWorkspace(false); setCurrentWorkspace(null); setBootstrapping(false);
        return;
      }
      const directory = await api.workspaces();
      const scopedMember = await hydrateWorkspaces(result.member, directory);
      setMember(scopedMember);
      // Keep the boot splash until member + Workspace context are both ready. This prevents the
      // transient Login screen visible in the Android recording while Workspace hydration runs.
      setBootstrapping(false);
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 401) console.error(error);
      const status = await api.bootstrapStatus();
      setEnvironment(status.environment); setBootstrapping(status.needsBootstrap); setMember(null); setWorkspaces([]); setCanCreateWorkspace(false); setCurrentWorkspace(null);
    }
  }

  function applyNavigation(next: AppRoute, replace = false) {
    const path = pathForRoute(next);
    if (replace) {
      window.history.replaceState(appHistoryState(window.history.state, historyIndexRef.current, window.scrollY), '', path);
    } else {
      window.history.replaceState(appHistoryScrollState(window.history.state, window.scrollY), '', window.location.href);
      const nextIndex = nextAppHistoryIndex(historyIndexRef.current);
      historyIndexRef.current = nextIndex;
      window.history.pushState(appHistoryState(null, nextIndex, 0), '', path);
      window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'auto' }));
    }
    routeRef.current = next;
    setRoute(next);
  }
  function navigate(next: AppRoute, replace = false) {
    if (closeOverlayBeforeNavigation(() => navigate(next, replace))) return;
    if (pathForRoute(next) === pathForRoute(routeRef.current)) {
      if (replace) applyNavigation(next, true);
      return;
    }
    if (!confirmDiscard()) return;
    setAccessChangedNotice(false);
    applyNavigation(next, replace);
  }
  useEffect(() => {
    const existingIndex = readAppHistoryIndex(window.history.state);
    if (existingIndex !== null) {
      historyIndexRef.current = existingIndex;
      return;
    }
    window.history.replaceState(appHistoryState(window.history.state, historyIndexRef.current, window.scrollY), '', window.location.href);
  }, []);
  useEffect(() => { routeRef.current = route; }, [route]);
  useEffect(() => {
    const onUnauthorized = () => {
      const currentRoute = routeRef.current;
      setMember(null); setWorkspaces([]); setCanCreateWorkspace(false); setCurrentWorkspace(null); setAccessChangedNotice(false);
      if (currentRoute.screen === 'invite') return;
      applyNavigation({ screen: 'home' }, true);
    };
    const onAccessChanged = () => {
      setAccessChangedNotice(true);
      loadSession().catch(console.error);
    };
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
    window.addEventListener(AUTH_ACCESS_CHANGED_EVENT, onAccessChanged);
    return () => {
      window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
      window.removeEventListener(AUTH_ACCESS_CHANGED_EVENT, onAccessChanged);
    };
  }, []);
  useEffect(() => { preloadScreen(route.screen); }, []);
  useEffect(() => { loadSession().catch(console.error); }, []);
  useEffect(() => {
    if (!member || !currentWorkspaceId) return;
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
    if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType ?? '')) return;
    const timer = window.setTimeout(() => {
      (['home', 'library', 'recipes', 'dishes', 'menus', 'scaling', 'more'] as AppScreen[]).forEach(preloadScreen);
    }, 700);
    return () => window.clearTimeout(timer);
  }, [member?.id, currentWorkspaceId]);
  useEffect(() => {
    const onPopState = (event: PopStateEvent) => {
      if (handleOverlayPopState(event.state)) return;
      const next = parseAppRoute(window.location.pathname);
      const targetIndex = readAppHistoryIndex(event.state);

      if (restoringHistoryRef.current) {
        restoringHistoryRef.current = false;
        if (targetIndex !== null) historyIndexRef.current = targetIndex;
        routeRef.current = next;
        setRoute(next);
        return;
      }

      const currentIndex = historyIndexRef.current;
      if (!confirmDiscard()) {
        const delta = restorationDelta(currentIndex, targetIndex);
        if (delta !== null && delta !== 0) {
          restoringHistoryRef.current = true;
          window.history.go(delta);
        } else {
          window.history.replaceState(appHistoryState(window.history.state, currentIndex), '', pathForRoute(routeRef.current));
        }
        return;
      }

      if (targetIndex !== null) historyIndexRef.current = targetIndex;
      routeRef.current = next;
      setAccessChangedNotice(false);
      setRoute(next);
      const targetScrollY = readAppHistoryScrollY(event.state) ?? 0;
      window.requestAnimationFrame(() => window.scrollTo({ top: targetScrollY, behavior: 'auto' }));
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [confirmDiscard]);

  const permissions = member?.permissions ?? [];
  const access = useMemo(() => ({
    recipeView: permissions.includes('recipe.view'), recipeEdit: permissions.includes('recipe.edit'), recipePublish: permissions.includes('recipe.publish'), recipeCopy: permissions.includes('recipe.copy'), recipeExport: permissions.includes('recipe.export'),
    researchView: permissions.includes('research.view'), researchEdit: permissions.includes('research.edit'),
    menuView: permissions.includes('menu.view'), menuEdit: permissions.includes('menu.edit'), menuPublish: permissions.includes('menu.publish'), menuCopy: permissions.includes('menu.copy'), menuExport: permissions.includes('menu.export'),
    dataExportAll: permissions.includes('data.export_all'), historyRestore: permissions.includes('history.restore'),
    memberManage: permissions.includes('member.manage'), permissionManage: permissions.includes('permission.manage'), auditView: permissions.includes('audit.view'),
    mediaRestore: permissions.includes('recycle.restore'), mediaPurge: permissions.includes('recycle.purge'),
    costView: permissions.includes('cost.view'), costExport: permissions.includes('cost.export')
  }), [permissions]);
  const shellAccess: ShellAccess = access;
  const isOwner = member?.roles.includes('owner') ?? false;
  const currentWorkspace = useMemo(() => workspaces.find(item => item.id === currentWorkspaceId) ?? null, [workspaces, currentWorkspaceId]);
  const routeAllowed = canOpenRoute(route.screen, shellAccess, isOwner);

  async function switchWorkspace(workspaceId: string) {
    if (!member || workspaceId === currentWorkspaceId) return;
    const target = workspaces.find(item => item.id === workspaceId && item.status === 'active' && item.membershipStatus === 'active');
    if (!target) throw new Error(locale === 'zh-CN' ? '这个餐厅空间当前不可访问' : 'This Workspace is not currently accessible.');
    if (!confirmDiscard()) return;
    const previous = currentWorkspaceId;
    setActiveWorkspaceId(workspaceId);
    try {
      const refreshed = await api.me();
      setMember(refreshed.member); setCurrentWorkspace(workspaceId); setAccessChangedNotice(false);
      navigate({ screen: 'home' }, true);
    } catch (error) {
      setActiveWorkspaceId(previous);
      throw error;
    }
  }

  async function createWorkspace(input: CreateWorkspaceInput) {
    if (!member) throw new Error(locale === 'zh-CN' ? '请先登录' : 'Please sign in first.');
    if (!canCreateWorkspace) throw new Error(locale === 'zh-CN' ? '当前 Account 没有创建新 Workspace 的资格' : 'This Account cannot create new Workspaces.');
    if (!confirmDiscard()) throw new Error(locale === 'zh-CN' ? '还有未保存的修改，已取消切换餐厅空间' : 'Unsaved changes are still present, so the Workspace switch was cancelled.');
    const created = await api.createWorkspace(input);
    setActiveWorkspaceId(created.id);
    try {
      const [directory, refreshed] = await Promise.all([api.workspaces(), api.me()]);
      setWorkspaces(directory.workspaces); setCanCreateWorkspace(directory.canCreateWorkspace); setCurrentWorkspace(created.id); setMember(refreshed.member); setAccessChangedNotice(false);
      navigate({ screen: 'home' }, true);
    } catch (error) {
      setCurrentWorkspace(created.id);
      throw error;
    }
  }

  async function handleWorkspaceChanged(_workspace: WorkspaceSettings, archived: boolean) {
    await refreshWorkspaceContext(archived ? null : currentWorkspaceId);
    if (archived) {
      setAccessChangedNotice(false);
      applyNavigation({ screen: 'home' }, true);
    }
  }

  async function handleWorkspaceRestored(workspace: WorkspaceSettings, enter = false) {
    await refreshWorkspaceContext(enter ? workspace.id : currentWorkspaceId);
    if (enter) {
      setAccessChangedNotice(false);
      applyNavigation({ screen: 'home' }, true);
    }
  }

  async function signOut() { if (!confirmDiscard()) return; await api.logout(); setActiveWorkspaceId(null); setMember(null); setWorkspaces([]); setCanCreateWorkspace(false); setCurrentWorkspace(null); setAccessChangedNotice(false); applyNavigation({ screen: 'home' }, true); }
  async function signOutForInvitation() { await api.logout(); setActiveWorkspaceId(null); setMember(null); setWorkspaces([]); setCanCreateWorkspace(false); setCurrentWorkspace(null); setAccessChangedNotice(false); }
  function handleLogin(next: CurrentMember) {
    applyAccountLocale(next.preferredLocale);
    setMember(next); setAccessChangedNotice(false);
    if (!next.mustChangePassword) hydrateWorkspaces(next).then(setMember).catch(console.error);
  }
  async function loginForInvitation(email: string, password: string) {
    const result = await api.login(email, password);
    applyAccountLocale(result.member.preferredLocale);
    setMember(result.member); setAccessChangedNotice(false);
    if (result.member.mustChangePassword) { setWorkspaces([]); setCanCreateWorkspace(false); setCurrentWorkspace(null); }
  }
  async function completeInvitation(workspaceId: string, nextMember: CurrentMember | null) {
    setActiveWorkspaceId(workspaceId);
    if (nextMember) { applyAccountLocale(nextMember.preferredLocale); setMember(nextMember); }
    const [directory, refreshed] = await Promise.all([api.workspaces(), api.me()]);
    setWorkspaces(directory.workspaces); setCanCreateWorkspace(directory.canCreateWorkspace); setCurrentWorkspace(workspaceId); setMember(refreshed.member); setAccessChangedNotice(false);
    applyAccountLocale(refreshed.member.preferredLocale);
    applyNavigation({ screen: 'home' }, true);
  }
  function handleForcedPasswordChanged(next: CurrentMember) {
    setMember(next);
    hydrateWorkspaces(next).then(setMember).catch(error => {
      console.error(error);
      loadSession().catch(console.error);
    });
  }
  async function persistLocale(next: UiLocale) {
    if (!member || member.mustChangePassword) throw new Error(locale === 'zh-CN' ? '当前账号状态不能保存界面语言' : 'The current Account state cannot save interface language.');
    const result = await api.updateAccountProfile({ displayName: member.displayName, preferredLocale: next });
    setMember(result.member);
  }

  if (bootstrapping === null) return <div className="loading-screen">Menu Lab</div>;
  if (bootstrapping) return <BootstrapPage onComplete={() => { setBootstrapping(false); loadSession().catch(console.error); }} />;
  if (member?.mustChangePassword) return <ForcedPasswordChangePage member={member} onMemberChange={handleForcedPasswordChanged} onLogout={() => { signOut().catch(console.error); }} />;
  if (route.screen === 'invite' && route.entityId) return <InviteAcceptPage
    token={route.entityId}
    member={member}
    onLogin={loginForInvitation}
    onAccepted={completeInvitation}
    onChangeAccount={signOutForInvitation}
  />;
  if (!member) return <LoginPage onLogin={handleLogin} />;
  if (!currentWorkspaceId) return <NoWorkspacePage
    member={member}
    workspaces={workspaces}
    canCreateWorkspace={canCreateWorkspace}
    onCreateWorkspace={createWorkspace}
    onWorkspaceRestored={workspace => handleWorkspaceRestored(workspace, true)}
    onLogout={signOut}
  />;

  const remoteTaskJobsEnabled = access.recipeExport || access.menuExport || access.dataExportAll || access.historyRestore;

  return <TaskProvider remoteJobsEnabled={remoteTaskJobsEnabled} workspaceKey={currentWorkspaceId}><AppShell
    member={member} environment={environment} route={route} access={shellAccess} navigate={navigate}
    workspaces={workspaces} currentWorkspaceId={currentWorkspaceId} canCreateWorkspace={canCreateWorkspace}
    onWorkspaceSwitch={switchWorkspace} onWorkspaceCreate={createWorkspace}
  >
    {accessChangedNotice && <p className="success-note" role="status">{locale === 'zh-CN' ? '你的 Workspace 权限刚刚发生变化，系统已按最新权限刷新。未成功提交的操作不会被视为已保存。' : 'Your Workspace access just changed. The app refreshed to the latest permissions; operations that did not succeed were not saved.'}</p>}
    {!routeAllowed ? <AccessDeniedState locale={locale} onHome={() => applyNavigation({ screen: 'home' }, true)} /> : <Suspense fallback={<PageLoadingSkeleton label={locale === 'zh-CN' ? '正在载入当前页面…' : 'Loading this page…'} />}>
      {route.screen === 'home' && <HomePage member={member} access={shellAccess} navigate={navigate} />}
      {route.screen === 'library' && <LibraryPage
        canEdit={access.recipeEdit}
        canCopy={access.recipeCopy}
        workspaces={workspaces}
        currentWorkspaceId={currentWorkspaceId}
        navigate={navigate}
      />}
      {route.screen === 'ingredients' && <IngredientsPage canEdit={access.recipeEdit} canCopy={access.recipeCopy} canRestore={access.mediaRestore} canPurge={access.mediaPurge} workspaces={workspaces} currentWorkspaceId={currentWorkspaceId} initialId={route.entityId} navigate={navigate} />}
      {route.screen === 'recipes' && <RecipesPage canEdit={access.recipeEdit} canPublish={access.recipePublish} canCopy={access.recipeCopy} canExport={access.recipeExport} canViewCost={access.costView} canExportCost={access.costExport} canViewValidation={access.researchView} canEditValidation={access.researchEdit} canRestore={access.mediaRestore} canPurge={access.mediaPurge} workspaces={workspaces} currentWorkspaceId={currentWorkspaceId} initialId={route.entityId} initialVersionId={route.versionId} initialView={route.view} onRoute={(entityId, versionId, view) => navigate({ screen: 'recipes', entityId, versionId, view })} />}
      {route.screen === 'dishes' && <DishesPage
        canEdit={access.recipeEdit} canPublish={access.recipePublish} canCopy={access.recipeCopy} canExport={access.recipeExport} canViewCost={access.costView} canExportCost={access.costExport} canReviewAllergens={access.researchEdit}
        canRestore={access.mediaRestore} canPurge={access.mediaPurge} workspaces={workspaces} currentWorkspaceId={currentWorkspaceId} initialId={route.entityId} initialVersionId={route.versionId} initialView={route.view}
        onRoute={(entityId, versionId, view) => navigate({ screen: 'dishes', entityId, versionId, view })} />}
      {route.screen === 'menus' && <MenusPage
        canEdit={access.menuEdit} canPublish={access.menuPublish} canCopy={access.menuCopy} canWorkspaceCopy={access.menuCopy && access.recipeCopy} canExport={access.menuExport} canViewCost={access.costView} canExportCost={access.costExport}
        canRestore={access.mediaRestore} canPurge={access.mediaPurge} workspaces={workspaces} currentWorkspaceId={currentWorkspaceId} initialId={route.entityId} initialVersionId={route.versionId} initialView={route.view}
        onRoute={(entityId, versionId, view) => navigate({ screen: 'menus', entityId, versionId, view })} />}
      {route.screen === 'imports' && <ImportPage canEdit={access.researchEdit} canApply={access.recipeEdit} initialId={route.entityId} navigate={navigate} />}
      {route.screen === 'scaling' && <ScalingPage canCopy={access.recipeCopy} canExport={access.recipeExport} />}
      {route.screen === 'exports' && <ExportCenterPage canRestore={access.historyRestore} />}
      {route.screen === 'members' && <MembersPage
        isOwner={isOwner}
        canResetPasswords={isOwner}
        currentMemberId={member.id}
        workspaceName={currentWorkspace?.name ?? 'Workspace'}
        onCurrentAccessChange={async () => { const refreshed = await api.me(); setMember(refreshed.member); }}
      />}
      {route.screen === 'roles' && <RolesPage canEdit={isOwner} />}
      {route.screen === 'audit' && <AuditPage isOwner={isOwner} />}
      {route.screen === 'trash' && <DomainTrashPage canRestore={access.mediaRestore} canPurge={access.mediaPurge} navigate={navigate} />}
      {route.screen === 'media-recycle' && <MediaRecyclePage canRestore={access.mediaRestore} canPurge={access.mediaPurge} />}
      {route.screen === 'workspace-settings' && <WorkspaceSettingsPage
        workspaceId={currentWorkspaceId}
        workspaces={workspaces}
        onWorkspaceChanged={handleWorkspaceChanged}
        onWorkspaceRestored={workspace => handleWorkspaceRestored(workspace, false)}
      />}
      {route.screen === 'account' && <AccountPage member={member} onMemberChange={setMember} />}
      {route.screen === 'more' && <MorePage member={member} access={shellAccess} navigate={navigate} onLocaleChange={persistLocale} onLogout={() => { signOut().catch(console.error); }} />}
    </Suspense>}
  </AppShell></TaskProvider>;
}

void ACTIVE_WORKSPACE_STORAGE_KEY;
